import { BaseApiService } from './base-api.service';

export type EventFilterType = 'all' | 'incomplete' | 'disabled' | 'warning';
export type EventDestinationType = 'all' | 'mfi' | 'qb' | 'stripe' | 'drip' | 'icontact';

// Types for API responses
export interface RetryEvent {
    id: number;
    description: string;
    functionName: string;
    retryCount: number;
    nextRetryTime: string | null;
    lastError: string;
    failedStep: string;
}

export interface MonitorData {
    activeCount: number;
    retryCount: number;
    retryQueue: RetryEvent[];
}

export interface HealthStatus {
    status: string;
    timestamp: string;
}

// Event Stats types
export interface EventStatsInterval {
    timestamp: string;
    success: number;
    warning: number;
    retry: number;
}

export interface EventStatsResponse {
    intervals: EventStatsInterval[];
    timeRange: {
        startTime: string;
        endTime: string;
        hoursRequested: number;
        intervalMinutes: number;
    };
}

// API Call Stats types
export interface ApiCallStatsInterval {
    timestamp: string;
    success: number;
    failure: number;
}

export interface ApiCallStatsResponse {
    intervals: ApiCallStatsInterval[];
    timeRange: {
        startTime: string;
        endTime: string;
        hoursRequested: number;
        intervalMinutes: number;
    };
}

export interface ForceRetryResponse {
    success: boolean;
    message: string;
}

export interface ForceDeadResponse {
    success: boolean;
    message: string;
}

export interface ForceHandledResponse {
    success: boolean;
    message: string;
}

// Event types
export interface Event {
    id: number;
    functionName: string;
    status: string;
    description: string;
    method: string;
    path: string;
    originatorName: string;
    originatorIP: string;
    createdTime: string;
    completedTime: string | null;
    executionMs: number | null;
    retryCount: number;
    nextRetryTime: string | null;
    failedStep: string | null;
    lastError: string | null;
    outcome: string | null;
    hasTimedOut: boolean;
    body?: string; // JSON string of the request/response body
}

export interface EventsResponse {
    events: Event[];
    pagination: {
        pageSize: number;
        hasMore: boolean;
        nextCursor: string | null;
        count: number;
    };
}

// API Call types
export type ApiCallFilterType = 'all' | 'success' | 'failure';

export interface ApiCall {
    id: number;
    method: string;
    path: string;
    description: string;
    statusCode: number;
    originator: string;
    originatorIP: string;
    createdTime: string;
}

export interface ApiCallFull extends ApiCall {
    requestBody: string;
    replyBody: string;
}

export interface ApiCallsResponse {
    apiCalls: ApiCall[];
    pagination: {
        pageSize: number;
        hasMore: boolean;
        nextCursor: string | null;
        count: number;
    };
}

// API Service class for server communication
class MonitorApiService extends BaseApiService {
    // Health check
    async getHealth(): Promise<HealthStatus> {
        const response = await this.client.get<HealthStatus>('/health');
        return response.data;
    }

    // Monitor data
    async getMonitorData(): Promise<MonitorData> {
        const response = await this.client.get<MonitorData>('/api/monitor/current');
        return response.data;
    }

    // Event statistics for activity chart
    async getEventStats(hours: number = 24): Promise<EventStatsResponse> {
        const response = await this.client.get<EventStatsResponse>(`/api/monitor/event-stats?hours=${hours}`);
        return response.data;
    }

    // API Call statistics for activity chart
    async getApiCallStats(hours: number = 24): Promise<ApiCallStatsResponse> {
        const response = await this.client.get<ApiCallStatsResponse>(`/api/monitor/api-call-stats?hours=${hours}`);
        return response.data;
    }

    // Force retry of a specific event
    async forceRetry(eventId: number): Promise<ForceRetryResponse> {
        const response = await this.client.post<ForceRetryResponse>(`/api/monitor/retry/${eventId}`);
        return response.data;
    }

    // Mark an event as dead
    async markDead(eventId: number, userEmail?: string): Promise<ForceDeadResponse> {
        const response = await this.client.post<ForceDeadResponse>(`/api/monitor/dead/${eventId}`, { userEmail });
        return response.data;
    }

    // Mark an event as handled
    async markHandled(eventId: number, userEmail?: string): Promise<ForceHandledResponse> {
        const response = await this.client.post<ForceHandledResponse>(`/api/monitor/handled/${eventId}`, { userEmail });
        return response.data;
    }

    // Mark an event as warning
    async markWarning(eventId: number, userEmail?: string): Promise<ForceHandledResponse> {
        const response = await this.client.post<ForceHandledResponse>(`/api/monitor/warning/${eventId}`, { userEmail });
        return response.data;
    }

    // Update event body
    async updateEventBody(
        eventId: number,
        body: string,
        userEmail?: string,
    ): Promise<{ success: boolean; message: string }> {
        const response = await this.client.patch<{ success: boolean; message: string }>(
            `/api/monitor/events/${eventId}/body`,
            { body, userEmail },
        );
        return response.data;
    }

    // Get paginated events
    async getEvents(
        pageSize: number = 50,
        cursor?: string,
        filter: EventFilterType = 'all',
        destination: EventDestinationType = 'all',
        search?: string,
    ): Promise<EventsResponse> {
        const params = new URLSearchParams();
        params.append('pageSize', pageSize.toString());
        if (cursor) {
            params.append('cursor', cursor);
        }
        params.append('filter', filter);
        params.append('destination', destination);
        if (search) {
            params.append('search', search);
        }

        const response = await this.client.get<EventsResponse>(`/api/monitor/events?${params}`);
        return response.data;
    }

    // Get single event by ID
    async getEvent(eventId: number): Promise<{ event: Event }> {
        const response = await this.client.get<{ event: Event }>(`/api/monitor/events/${eventId}`);
        return response.data;
    }

    // Get paginated API calls
    async getApiCalls(
        pageSize: number = 50,
        cursor?: string,
        filter: ApiCallFilterType = 'all',
        search?: string,
    ): Promise<ApiCallsResponse> {
        const params = new URLSearchParams();
        params.append('pageSize', pageSize.toString());
        if (cursor) {
            params.append('cursor', cursor);
        }
        params.append('filter', filter);
        if (search) {
            params.append('search', search);
        }

        const response = await this.client.get<ApiCallsResponse>(`/api/monitor/api-calls?${params}`);
        return response.data;
    }

    // Get single API call by ID
    async getApiCall(apiCallId: number): Promise<{ apiCall: ApiCallFull }> {
        const response = await this.client.get<{ apiCall: ApiCallFull }>(`/api/monitor/api-calls/${apiCallId}`);
        return response.data;
    }
}

// Export singleton instance
export const monitorApi = new MonitorApiService();
